import { api } from '../api/client';
import { API_COLLECTIONS_URL, API_URL } from '../utils';
import { apiErrorCode, apiErrorMessage } from '../utils/apiErrors';
import { noteCollectionsChanged } from '../contexts/CollectionsContext';

// Transfer offers (KPR-743; keepr-api docs/SCHEMA.md "Transfer offers"): a
// collection changes owner only when the person it is offered to accepts.
// The owner offers it from the collection's Danger zone; the recipient answers
// on the offer page (/transfers/:offerId), from Home, or from the notice's
// buttons. Every door is a session's. Reads that meet a 404 from an API older
// than this feature answer "no offers", so a web build ahead of the API
// degrades instead of breaking.

export const API_TRANSFER_OFFERS_URL = `${API_URL}/transfer-offers`;

const isNotFound = error => error?.response?.status === 404;

/** `{ incoming, outgoing }` — the caller's pending offers both ways. */
export async function listTransferOffers() {
	try {
		const { data } = await api.get(API_TRANSFER_OFFERS_URL);
		return {
			incoming: Array.isArray(data?.incoming) ? data.incoming : [],
			outgoing: Array.isArray(data?.outgoing) ? data.outgoing : [],
		};
	} catch (error) {
		if (isNotFound(error)) return { incoming: [], outgoing: [] };
		throw error;
	}
}

/** One offer, any status. A 404 (not there, or not the caller's) propagates. */
export async function getTransferOffer(offerId) {
	const { data } = await api.get(`${API_TRANSFER_OFFERS_URL}/${encodeURIComponent(offerId)}`);
	return data?.offer || null;
}

/** The recipient says yes → `{ offer, collection, transferred }`. */
export async function acceptTransferOffer(offerId) {
	const { data } = await api.post(`${API_TRANSFER_OFFERS_URL}/${encodeURIComponent(offerId)}/accept`);
	noteCollectionsChanged(); // the collection (and its family) is the recipient's now
	return data;
}

/** The recipient says no → the offer. */
export async function declineTransferOffer(offerId) {
	const { data } = await api.post(`${API_TRANSFER_OFFERS_URL}/${encodeURIComponent(offerId)}/decline`);
	return data?.offer || null;
}

/** Whoever offered it takes it back → the offer. */
export async function cancelTransferOffer(offerId) {
	const { data } = await api.post(`${API_TRANSFER_OFFERS_URL}/${encodeURIComponent(offerId)}/cancel`);
	return data?.offer || null;
}

/** The collection's pending offer, or null (the owner's read; null from an older API too). */
export async function getCollectionTransferOffer(collectionId) {
	try {
		const { data } = await api.get(`${API_COLLECTIONS_URL}/${collectionId}/transfer`);
		return data?.offer || null;
	} catch (error) {
		// An API before KPR-743 has no GET here (405/404): no offer to show.
		if (isNotFound(error) || error?.response?.status === 405) return null;
		throw error;
	}
}

/** The owner offers the collection to `newOwnerId` → the offer. */
export async function offerCollection(collectionId, newOwnerId) {
	const { data } = await api.post(`${API_COLLECTIONS_URL}/${collectionId}/transfer`, { newOwnerId });
	return data?.offer || null;
}

// The refusals these doors give, in words a person can act on.
const WORDS = {
	transfer_pending: 'This collection is already offered to someone. Cancel that offer first.',
	transfer_offer_cooldown: 'That person declined this collection less than a day ago. Try again tomorrow.',
	transfer_offer_daily_limit: 'You have offered ten collections today. Try again tomorrow.',
	recipient_unverified: 'That person has not confirmed their email address yet, so they cannot be offered a collection.',
	invalid_recipient: 'That person cannot be offered this collection. Check that they have an active keepr account and are not the owner already.',
	recipient_kind_unsupported: 'Only a person can be offered a collection.',
	collection_not_active: 'An archived collection cannot be offered. Unarchive it first.',
	is_sub_collection: 'A sub-collection moves only with its parent collection. Offer the parent instead.',
	collection_published: 'A global collection cannot be offered while it is published or retiring. Retire it first.',
	personal_collection_locked: 'Your personal collection cannot be given to anyone.',
	email_unverified: 'Confirm your email address to offer a collection.',
	offer_not_found: 'This offer is not here. It may have been made to someone else.',
	offer_not_pending: 'This offer has already been answered or withdrawn.',
	offer_expired: 'This offer has ended.',
	offer_not_yours: 'Only the person who made the offer can cancel it.',
};

/** Words for a refusal from any transfer-offer door. */
export function transferOfferRefusal(error, fallback = 'That did not work. Please try again.') {
	const code = apiErrorCode(error);
	if (code === 'transfer_offer_closed') {
		const reason = error?.response?.data?.message;
		return typeof reason === 'string' && reason ? reason : 'This offer could not go ahead, so it is closed.';
	}
	if (code && WORDS[code]) return WORDS[code];
	return apiErrorMessage(error, fallback);
}
